import { BufferedAction } from "./bufferedAction.js";
import { EventHandler } from "./EventHandler.js";
export class LinkedDataListItem {
    constructor(data: Record<string, number>, prevPoint: LinkedDataListItem = undefined, anchorPoint = false) {
        this.data = data;
        this.prevPoint = prevPoint;
        this.anchorPoint = anchorPoint;
    }
    prevPoint: LinkedDataListItem | undefined = undefined;
    anchorPoint = false;
    anchorData = undefined;
    data: Record<string, number>;
    getAllData(): Record<string, number> {
        if (this.anchorPoint && this.anchorData) {
            return Object.assign({ ...this.anchorData }, this.data);
        }
        if (this.prevPoint === undefined) {
            return this.data;
        } else {
            return Object.assign({ ...this.prevPoint.getAllData() }, this.data);
        }
    }
}

export class PermanentlySortedList<DataType = number> {
    //ascending order
    delayOnAdditions = 80;
    value: DataType[] = [];
    k = 0;
    queue: DataType[] = [];
    getValue = undefined;
    onPushFinished = new EventHandler();
    filter = undefined;
    pushBufferedAction = undefined;
    /**
     * @param valueFunction - A function that returns the numeric value for each item in the list.
     * @param filter (optional) - A filter function for each item in the list, returns false if item should be filtered.
     */
    constructor(
        valueFunction: (v: DataType) => number = (v: DataType): number => v as number,
        filter: (a: DataType) => boolean = (_a: DataType): boolean => true,
    ) {
        this.getValue = valueFunction;
        this.filter = filter;
        this.pushBufferedAction = new BufferedAction(
            () => {
                const newEvents = [...this.queue];
                this.value = this.__doPush();
                this.onPushFinished.trigger({ newValues: newEvents });
            },
            80,
            false,
            true,
        );
    }
    __doPush(): DataType[] {
        const tempQueue = [...this.queue];
        const newValue = [...this.value];
        this.queue = [];
        if (tempQueue.length > this.value.length / 3.0) {
            //TODO: Optimize better
            tempQueue.push(...this.value);
            tempQueue.sort((a, b) => this.getValue(a) - this.getValue(b));
            newValue.splice(0, this.value.length, ...tempQueue);
        } else {
            tempQueue.forEach((newItem) => {
                insertIntoSortedList(newValue, newItem, this.getValue);
            });
        }
        if (this.filter) {
            newValue.filter(this.filter);
        }
        return newValue;
    }
    push(...items: DataType[]): void {
        this.queue.push(...items);
        this.pushBufferedAction.trigger();
        // setTimeout(() => {
        //     if (this.k === temp) {
        //         console.log(`Doing push to perm list ${Date.now()%10000}`)
        //         const newEvents = [...this.queue];
        //         this.value = this.__doPush();
        //         console.log(`Done push to perm list ${Date.now()%10000}`)
        //         this.onPushFinished.trigger({ newValues: newEvents }).then(()=>{
        //
        //             console.log(`Done event handler for push finished ${Date.now()%10000}`)
        //         });
        //     }
        // }, this.delayOnAdditions);
    }

    /**
     * Executes a binary search to find closest point to the value specified.
     * @param target
     * @param force
     */
    getClosestPointToValue(target: number, force: "lower" | "higher" | "none" = "none"): DataType | undefined {
        if (this.value.length < 1) {
            return undefined;
        }
        if (this.value.length < 2) {
            return this.value[0];
        }
        if (force === "lower" && this.getValue(this.value[0]) > target) {
            return undefined;
        }
        if (force === "higher" && this.getValue(this.value[this.value.length - 1]) < target) {
            return undefined;
        }
        let a = 0;
        let b = this.value.length - 1;
        let i = Math.floor((a + b) / 2);
        while (b - a > 4) {
            i = Math.floor((a + b) / 2);
            const iValue = this.getValue(this.value[i]);
            if (iValue > target) {
                b = i;
            } else if (iValue < target) {
                a = i;
            } else {
                a = i;
                b = i;
                break;
            }
        }
        if (b === a) {
            return this.value[b];
        }
        let closestPoint = this.value[a];
        let closestDelta = 1000000000000000000000000000;
        for (let x = Math.max(a, 0); x <= b && x < this.value.length; x++) {
            const cursorValue = this.getValue(this.value[x]);
            if ((force !== "lower" || cursorValue < target) && (force !== "higher" || cursorValue > target)) {
                const newDelta = Math.abs(cursorValue - target);
                if (newDelta < closestDelta) {
                    closestDelta = newDelta;
                    closestPoint = this.value[x];
                }
            }
        }
        return closestPoint;
    }
    clear(): void {
        this.value = [];
        this.queue = [];
    }
}

export class PermSortedLinkedList<DataType extends LinkedDataListItem> extends PermanentlySortedList<DataType> {
    spaceBetweenAnchors = 15;
    /**
     * Percentage (between 0 and 1) of error allowed in anchor spacing.
     * WIth a value of 0.5 and a spaceBetweenAnchors of 10, the space between anchors will
     * always be between 5 and 15. With a value of 0.3 the spacing would alwasy be between 7 and 13
     * @type {number}
     */
    MarginOnAnchorSpace = 0.5;

    validate(list: DataType[]): DataType[] {
        list = list.filter((v) => v !== null && v !== undefined);
        if (list.length < 1) {
            return list;
        }
        const firstItem = list[0];
        if (!firstItem.anchorPoint) {
            firstItem.anchorPoint = true;
        }
        let lastAnchorPosition = 0;
        const nextAnchorPointData = { ...firstItem.data };
        for (let i = 1; i < list.length; i++) {
            if (list[i].prevPoint !== list[i - 1]) {
                list[i].prevPoint = list[i - 1];
            }
            if (i - lastAnchorPosition < this.spaceBetweenAnchors) {
                Object.assign(nextAnchorPointData, list[i].data);
            }
            if (list[i].anchorPoint) {
                list[i].anchorData = { ...nextAnchorPointData };
                lastAnchorPosition = i;
            }
            if (
                i - lastAnchorPosition >
                this.spaceBetweenAnchors + this.spaceBetweenAnchors * this.MarginOnAnchorSpace
            ) {
                const newAnchor = list[lastAnchorPosition + this.spaceBetweenAnchors];
                lastAnchorPosition = lastAnchorPosition + this.spaceBetweenAnchors;
                newAnchor.anchorPoint = true;
                newAnchor.anchorData = { ...nextAnchorPointData };
            }
        }
        return list;
    }

    __doPush(): DataType[] {
        const result = super.__doPush();
        return this.validate(result);
    }
}
function insertIntoSortedList(sortedList, newItem, valueFunction = (v) => v) {
    if (sortedList.length === 0) {
        sortedList.push(newItem);
        return sortedList;
    }
    let max = sortedList.length - 1;
    let min = 0;
    const newItemValue = valueFunction(newItem);
    if (valueFunction(sortedList[0]) > newItemValue) {
        sortedList.splice(0, 0, newItem);
        return sortedList;
    }
    if (valueFunction(sortedList[max]) < newItemValue) {
        sortedList.push(newItem);
        return sortedList;
    }
    while (max !== min) {
        if (max - min === 1) {
            sortedList.splice(max, 0, newItem);
            return sortedList;
        }
        const index = Math.floor((max + min) / 2);
        const indexValue = valueFunction(sortedList[index]);
        if (indexValue >= newItemValue) {
            max = index;
        }
        if (indexValue <= newItemValue) {
            min = index;
        }
    }
    sortedList.splice(max, 0, newItem);
    return sortedList;
}
